import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CallToAction from "@/components/CallToAction";
import candidatePhoto from "@/assets/candidate-about.jpg";

const About = () => {
  return (
    <>
      <Helmet>
        <title>About Matt | Matt Bucher for State Representative</title>
        <meta name="description" content="Learn about Matt Bucher, Republican candidate for Delaware's 33rd District. Vice President of the Milford School Board, fighting for rural values and local control." />
        <link rel="canonical" href="https://votebucher.com/about" />
        <meta property="og:url" content="https://votebucher.com/about" />
      </Helmet>
      
      <div className="min-h-screen flex flex-col">
        <Header />
        
        <main id="main-content" className="flex-grow">
          {/* Hero Banner */}
          <section className="bg-secondary py-20" aria-labelledby="about-heading">
            <div className="container mx-auto px-4 text-center">
              <h1 id="about-heading" className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-secondary-foreground mb-4">
                About Matt Bucher
              </h1>
              <p className="font-body text-xl text-secondary-foreground/80 max-w-2xl mx-auto">
                Republican candidate for Delaware's 33rd District House seat
              </p>
            </div>
          </section>

          {/* Bio Section */}
          <section className="py-16 bg-background" aria-labelledby="bio-heading">
            <div className="container mx-auto px-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                <div>
                  <img
                    src={candidatePhoto}
                    alt="Matt Bucher, Republican candidate for Delaware's 33rd District"
                    className="rounded-lg shadow-xl w-full max-w-md mx-auto lg:mx-0"
                    loading="lazy"
                  />
                </div>
                <div>
                  <h2 id="bio-heading" className="font-display text-3xl font-bold text-foreground mb-6">
                    A Lifetime of Service
                  </h2>
                  <div className="space-y-4 font-body text-lg text-muted-foreground">
                    <p>
                      Matt Bucher is the Republican candidate for Delaware's 33rd District House seat, running to succeed retiring Representative Charles Postles. Born in Western Kentucky, Bucher moved to lower Delaware nearly 30 years ago with his wife, Renee, choosing the area because it reminded them of their rural birthplaces. Their son, Ethan, a graduate of Milford High School is well-known throughout the local community for his academic, athletic, and civic pursuits.
                    </p>
                    <p>
                      Professionally, Bucher combines his career experience as a commercial construction estimator with decades of volunteerism, as well as his public service as Vice President of the Milford School Board and Legislative Chair of the Delaware School Boards Association. His platform prioritizes returning decision-making power to local school districts, municipalities, and county governments, fighting for fairness for Delaware taxpayers, small businesses, and farms, and defending constitutional rights.
                    </p>
                    <p>
                      Positioning himself as a fighter for rural and small-town values, Bucher pledges to defend Second Amendment rights, support election integrity measures like voter ID, and reduce the size and scope of state government to ensure the 33rd District is effectively represented in Dover.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Values Section */}
          <section className="py-16 bg-card" aria-labelledby="values-heading">
            <div className="container mx-auto px-4">
              <h2 id="values-heading" className="font-display text-3xl font-bold text-foreground mb-8 text-center">
                Matt's Values
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4" aria-hidden="true">
                    <span className="font-display text-3xl font-bold text-primary">1</span>
                  </div>
                  <h3 className="font-display text-xl font-bold text-foreground mb-2">Local Control</h3>
                  <p className="font-body text-muted-foreground">
                    Returning decision-making power to local school districts and reducing state government mandates.
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4" aria-hidden="true">
                    <span className="font-display text-3xl font-bold text-primary">2</span>
                  </div>
                  <h3 className="font-display text-xl font-bold text-foreground mb-2">Constitutional Rights</h3>
                  <p className="font-body text-muted-foreground">
                    Defending Second Amendment rights and supporting election integrity measures like voter ID.
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4" aria-hidden="true">
                    <span className="font-display text-3xl font-bold text-primary">3</span>
                  </div>
                  <h3 className="font-display text-xl font-bold text-foreground mb-2">Rural Values</h3>
                  <p className="font-body text-muted-foreground">
                    Fighting for the rural communities that make Delaware's 33rd District special.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Experience Section */}
          <section className="py-16 bg-background" aria-labelledby="experience-heading">
            <div className="container mx-auto px-4">
              <h2 id="experience-heading" className="font-display text-3xl font-bold text-foreground mb-8 text-center">
                Experience & Leadership
              </h2>
              <div className="max-w-3xl mx-auto">
                <ul className="space-y-6" role="list">
                  <li className="border-l-4 border-primary pl-6">
                    <h3 className="font-display text-xl font-bold text-foreground">Vice President, Milford School Board</h3>
                    <p className="font-body text-muted-foreground">Leading efforts to return decision-making to local districts</p>
                  </li>
                  <li className="border-l-4 border-primary pl-6">
                    <h3 className="font-display text-xl font-bold text-foreground">Legislative Chair, Delaware School Boards Association</h3>
                    <p className="font-body text-muted-foreground">Advocating for education policy at the state level</p>
                  </li>
                  <li className="border-l-4 border-primary pl-6">
                    <h3 className="font-display text-xl font-bold text-foreground">Chairman (Three Terms), 33rd District Republican Committee</h3>
                    <p className="font-body text-muted-foreground">Chaired the only district in Delaware to turn from "blue" to "red" in total voter registrations in the past twenty years</p>
                  </li>
                  <li className="border-l-4 border-primary pl-6">
                    <h3 className="font-display text-xl font-bold text-foreground">Commercial Construction Estimator</h3>
                    <p className="font-body text-muted-foreground">Nearly three decades of professional experience in the construction industry</p>
                  </li>
                  <li className="border-l-4 border-primary pl-6">
                    <h3 className="font-display text-xl font-bold text-foreground">Founder, Buccaneer Educational Foundation</h3>
                    <p className="font-body text-muted-foreground">Established to support educational initiatives in the community</p>
                  </li>
                  <li className="border-l-4 border-primary pl-6">
                    <h3 className="font-display text-xl font-bold text-foreground">Member, Milford Citizens Budget Oversight Committee</h3>
                    <p className="font-body text-muted-foreground">Providing citizen-led supervision to multi-million dollar budgets</p>
                  </li>
                  <li className="border-l-4 border-primary pl-6">
                    <h3 className="font-display text-xl font-bold text-foreground">Member, Board of Trustees, Delaware Agricultural Museum</h3>
                    <p className="font-body text-muted-foreground">Caring for our state's rich and diverse agricultural heritage</p>
                  </li>
                </ul>
                <p className="font-body text-muted-foreground mt-8 text-center max-w-2xl mx-auto">
                  From the Kent County Redistricting Commission to the Counselor's Parent Advisory Council, Matt also serves or has served on many other boards and committees.
                </p>
              </div>
            </div>
          </section>

          <CallToAction />
        </main>
        
        <Footer />
      </div>
    </>
  );
};

export default About;
